import React, { useState, useEffect } from 'react';
import { AvatarEnseignant } from './PhotoEnseignant';

/**
 * CHOISIR LE TUTEUR PÉDAGOGIQUE (lot 3.5a) — un seul composant pour les trois endroits où l'on
 * affecte : la liste de la ligne du tableau de bord stagiaire, le panneau ⑧ et le volet du tuteur.
 * Maquette : `#tuteurDropdown` et la section ⑧ du panneau (suivi_de_stage_maquette.html).
 *
 *  - en haut, les enseignants qui suivent DÉJÀ des étudiants, du plus chargé au moins chargé
 *    (spec §Panneau, E139), avec leur barre de charge ;
 *  - en dessous, TOUS les enseignants de l'année, y compris ceux à charge 0 ;
 *  - « X sera remplacé(e) par Y » avant un remplacement ; le bouton dit « Affecter » + prénom ;
 *  - « Retirer le tuteur » quand il y en a un.
 *
 * ⚠️ La charge affichée est TOUJOURS la charge totale de l'enseignant (stagiaires + alternants),
 * jamais celle d'un filtre (E143). Les couleurs par rapport au « nombre souhaité » arrivent avec
 * l'aide à la répartition (3.5b) : d'ici là, une seule couleur.
 */
const nom = (e) => (e ? `${e.nom} ${e.prenom || ''}`.trim() : '');

function StageChoixTuteur({ enseignants, actuelId = null, onAffecter, enCours = false, verrou = null, lectureSeule = null }) {
    const [choix, setChoix] = useState(actuelId);
    useEffect(() => { setChoix(actuelId); }, [actuelId]);

    if (verrou) return <div className="stg-conv-raison"><i className="fa-solid fa-lock" /> {verrou}</div>;
    const actifs = (enseignants || []).filter((e) => e.actif);
    if (!actifs.length) {
        return (
            <div className="stg-encart stg-encart-alerte">
                <i className="fa-solid fa-triangle-exclamation" />
                <span>Aucun enseignant pour cette année dans ce département : ajoutez-les dans le module <b>Enseignants</b>.</span>
            </div>
        );
    }
    const avecSuivi = actifs.filter((e) => e.charge > 0).sort((a, b) => b.charge - a.charge || a.nom.localeCompare(b.nom, 'fr'));
    const echelle = Math.max(5, ...actifs.map((e) => e.charge));
    const actuel = (enseignants || []).find((e) => e.id === actuelId) || null;
    const choisi = actifs.find((e) => e.id === choix) || null;
    const change = choisi && choisi.id !== actuelId;
    const fige = !!lectureSeule || enCours;

    return (
        <div className="stg-choix-tuteur">
            {avecSuivi.length > 0 && (
                <>
                    <div className="stg-choix-titre">Enseignants avec suivi actif :</div>
                    <div className="stg-choix-liste">
                        {avecSuivi.map((e) => (
                            <button type="button" key={e.id} disabled={fige}
                                    className={'stg-tut-row' + (e.id === choix ? ' choisi' : '') + (e.id === actuelId ? ' actuel' : '')}
                                    onClick={() => setChoix(e.id)} title={`${nom(e)} — ${e.charge} étudiant${e.charge > 1 ? 's' : ''} suivi${e.charge > 1 ? 's' : ''}`}>
                                <AvatarEnseignant e={e} className="stg-tut-av stg-tut-av-petit" />
                                <span className="stg-tut-nom">{nom(e)}</span>
                                <span className="stg-tut-barre"><span style={{ width: `${Math.min(100, (e.charge / echelle) * 100)}%` }} /></span>
                                <span className="stg-tut-n">{e.charge}{e.id === actuelId ? ' ✓' : ''}</span>
                            </button>
                        ))}
                    </div>
                </>
            )}
            <div className="stg-choix-tous">
                <label><i className="fa-solid fa-list" /> Tous les enseignants :</label>
                <select className="input-sm" value={choix || ''} disabled={fige} aria-label="Tous les enseignants"
                        onChange={(ev) => setChoix(ev.target.value ? Number(ev.target.value) : null)}>
                    <option value="">— Choisir —</option>
                    {[...actifs].sort((a, b) => a.nom.localeCompare(b.nom, 'fr')).map((e) => (
                        <option key={e.id} value={e.id}>{nom(e)} ({e.charge}){e.id === actuelId ? ' ← actuel' : ''}</option>
                    ))}
                </select>
            </div>
            {change && actuel && (
                <div className="stg-choix-alerte"><i className="fa-solid fa-triangle-exclamation" /> {nom(actuel)} sera remplacé(e) par {nom(choisi)}.</div>
            )}
            {lectureSeule && <div className="stg-conv-raison"><i className="fa-solid fa-circle-info" /> {lectureSeule}</div>}
            {change && (
                <button type="button" className="btn btn-green btn-sm stg-choix-bouton" disabled={fige} onClick={() => onAffecter(choisi.id)}>
                    {enCours ? <i className="fa-solid fa-circle-notch stg-tourne" /> : <i className="fa-solid fa-check" />} Affecter {choisi.prenom || choisi.nom}
                </button>
            )}
            {actuelId && !change && (
                <button type="button" className="stg-choix-retirer" disabled={fige} onClick={() => onAffecter(null)}
                        title="Le dossier n'aura plus de tuteur (et revient à « Affecter un tuteur » si ses documents sont validés)">
                    <i className="fa-solid fa-user-minus" /> Retirer le tuteur
                </button>
            )}
        </div>
    );
}

export default StageChoixTuteur;
